import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import './ProjectTracker.css'; // Styles for this component
import { SearchToken, SearchOpenDataGroup } from '../../types/api';

// Project option interface for our component
interface ProjectOption {
  name: string;
  type: 'PROJECT' | 'ECOSYSTEM';
  image?: string;
}

// Define the project form data interface
export interface ProjectTrackerFormData {
  project: string;
  projectName?: string;
  projectType?: string;
}

// Function to generate template string from form data
const generateTemplateString = (data: ProjectTrackerFormData): string => {
  // Format the project display with name and type if available
  const projectDisplay = data.projectName && data.projectType
    ? `${data.projectName} (${data.projectType.toLowerCase()})`
    : data.project;
  
  return `
  I want to track the progress and updates for ${projectDisplay}.
  Please provide the latest information about this project including recent developments,
  key milestones, and upcoming events.`;
};

// Extract header content for the Header component
export const getHeaderContent = () => {
  return {
    iconSrc: "/portfolio_management_icon.png", // Reusing the icon
    iconAlt: "Project Tracker Icon",
    title: "Project Tracker",
    subtitle: "Select a project you're interested in, and we'll provide you with the latest information"
  };
};

export interface ProjectTrackerHandle {
  getFormData: () => ProjectTrackerFormData | null;
  generateTemplate: () => string | null;
}

interface ProjectTrackerProps {
  onValidityChange?: (isValid: boolean) => void;
}

const ProjectTracker = forwardRef<ProjectTrackerHandle, ProjectTrackerProps>(({ onValidityChange }, ref) => {
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<ProjectOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Static project options for fallback
  const staticProjectOptions: ProjectOption[] = [
    { name: 'Berachain', type: 'ECOSYSTEM'},
    { name: 'Abstract', type: 'ECOSYSTEM' },
  ];

  // Load popular projects on component mount
  useEffect(() => {
    const loadPopularProjects = async () => {
      try {
        if (window.openDataApiClient && window.openDataApiClient.searchGrouped) {
          setIsLoading(true);
          // Fetch popular projects and ecosystems
          const response = await window.openDataApiClient.searchGrouped.searchGrouped({
            types: ["PROJECT", "ECOSYSTEM"],
            keyword: "", // Empty keyword to get popular items
            limit: 5
          });

          if (response && response.data) {
            // First collect all projects and ecosystems separately
            const ecosystemItems: ProjectOption[] = [];
            const projectItems: ProjectOption[] = [];
            
            // Process each group type and extract projects from their rows
            response.data.forEach((group: SearchOpenDataGroup) => {
              // Only include ECOSYSTEM and PROJECT types
              if (group.type === 'ECOSYSTEM' && group.rows && Array.isArray(group.rows)) {
                const ecosystemOptions = group.rows.map((item: SearchToken) => ({
                  name: item.name || 'Unknown',
                  type: 'ECOSYSTEM' as const,
                  image: item.image || undefined
                }));
                ecosystemItems.push(...ecosystemOptions);
              } else if (group.type === 'PROJECT' && group.rows && Array.isArray(group.rows)) {
                const projectOptions = group.rows.map((item: SearchToken) => ({
                  name: item.name || 'Unknown',
                  type: 'PROJECT' as const,
                  image: item.image || undefined
                }));
                projectItems.push(...projectOptions);
              }
            });
            
            // Merge items with the same name, preferring PROJECT images
            const mergedMap = new Map<string, ProjectOption>();
            
            // First add all ecosystem items to the map
            ecosystemItems.forEach(item => {
              mergedMap.set(item.name, item);
            });
            
            // Then add or merge project items
            projectItems.forEach(item => {
              const existingItem = mergedMap.get(item.name);
              if (existingItem) {
                // Merge: keep the PROJECT type and prefer the PROJECT image if available
                mergedMap.set(item.name, {
                  name: item.name,
                  type: 'ECOSYSTEM' as const, // Prefer PROJECT type when merging
                  image: item.image || existingItem.image // Prefer PROJECT image, fallback to ECOSYSTEM image
                });
              } else {
                // Add new project item
                mergedMap.set(item.name, item);
              }
            });
            
            // Convert map back to array
            const allProjects = Array.from(mergedMap.values());
            
            setProjectOptions(allProjects);
          }
        } else {
          // Fallback to static data if API is not available
          setProjectOptions(staticProjectOptions);
        }
      } catch (err) {
        console.error('Error loading popular projects:', err);
        // Fallback to static data
        setProjectOptions(staticProjectOptions);
      } finally {
        setIsLoading(false);
      }
    };

    loadPopularProjects();
  }, []);

  // Determine if form is valid
  const isFormValid = selectedProject !== null;

  // Report validity change to parent
  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isFormValid);
    }
  }, [isFormValid, onValidityChange]);

  // Function to search projects using the iframe API
  const searchProjects = async (keyword: string) => {
    if (!keyword || keyword.length < 2) {
      setFilteredOptions([]);
      setIsDropdownOpen(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if the API client is available in the window object
      if (window.openDataApiClient && window.openDataApiClient.searchGrouped) {
        const response = await window.openDataApiClient.searchGrouped.searchGrouped({
          types: ["PROJECT", "ECOSYSTEM"],
          keyword: keyword,
          operator: "PREFIX",
          limit: 10
        });

        // Process the response data
        if (response && response.data) {
          // First collect all projects and ecosystems separately
          const ecosystemItems: ProjectOption[] = [];
          const projectItems: ProjectOption[] = [];
          
          // Process each group type and extract projects from their rows
          response.data.forEach((group: SearchOpenDataGroup) => {
            // Only include ECOSYSTEM and PROJECT types
            if (group.type === 'ECOSYSTEM' && group.rows && Array.isArray(group.rows)) {
              const ecosystemOptions = group.rows.map((item: SearchToken) => ({
                name: item.name || 'Unknown',
                type: 'ECOSYSTEM' as const,
                image: item.image || undefined
              }));
              ecosystemItems.push(...ecosystemOptions);
            } else if (group.type === 'PROJECT' && group.rows && Array.isArray(group.rows)) {
              const projectOptions = group.rows.map((item: SearchToken) => ({
                name: item.name || 'Unknown',
                type: 'PROJECT' as const,
                image: item.image || undefined
              }));
              projectItems.push(...projectOptions);
            }
          });
          
          // Merge items with the same name, preferring PROJECT images
          const mergedMap = new Map<string, ProjectOption>();
          
          // First add all ecosystem items to the map
          ecosystemItems.forEach(item => {
            mergedMap.set(item.name, item);
          });
          
          // Then add or merge project items
          projectItems.forEach(item => {
            const existingItem = mergedMap.get(item.name);
            if (existingItem) {
              // Merge: keep the PROJECT type and prefer the PROJECT image if available
              mergedMap.set(item.name, {
                name: item.name,
                type: 'ECOSYSTEM' as const, // Prefer PROJECT type when merging
                image: item.image || existingItem.image // Prefer PROJECT image, fallback to ECOSYSTEM image
              });
            } else {
              // Add new project item
              mergedMap.set(item.name, item);
            }
          });
          
          // Convert map back to array
          const allProjects = Array.from(mergedMap.values());
          
          // Only update filtered options, not the candidate projects
          console.log(allProjects);
          setFilteredOptions(allProjects);
          setIsDropdownOpen(allProjects.length > 0);
        } else {
          setFilteredOptions([]);
          setIsDropdownOpen(false);
        }
      } else {
        // Fallback to static data if API is not available
        const filtered = staticProjectOptions.filter(option => 
          option.name.toLowerCase().includes(keyword.toLowerCase())
        );
        
        setProjectOptions(staticProjectOptions);
        setFilteredOptions(filtered);
        setIsDropdownOpen(filtered.length > 0);
      }
    } catch (err) {
      console.error('Error searching projects:', err);
      setError('Failed to fetch project data');
      
      // Fallback to static data
      const filtered = staticProjectOptions.filter(option => 
        option.name.toLowerCase().includes(keyword.toLowerCase())
      );
      setProjectOptions(staticProjectOptions);
      setFilteredOptions(filtered);
      setIsDropdownOpen(filtered.length > 0 && keyword.length > 0);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search to avoid too many API calls
  const debounce = <T extends (arg: string) => void>(func: T, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (arg: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(arg), delay);
    };
  };

  // Create a debounced search function
  const debouncedSearch = useRef(debounce(searchProjects, 300)).current;

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    
    // If the input is cleared, reset the selected project
    // This will make the form invalid and disable the Start Tracking button
    if (!value.trim()) {
      setSelectedProject(null);
      setFilteredOptions([]);
      setIsDropdownOpen(false);
    } else {
      debouncedSearch(value);
    }
  };
  
  const handleSelectProject = (option: ProjectOption) => {
    setSelectedProject(option);
    // Format the search term as NAME(TYPE)
    setSearchTerm(`${option.name}(${option.type})`);
    setIsDropdownOpen(false);
  };

  const handleCandidateProjectSelect = (projectName: string) => {
    // First check if the project exists in projectOptions
    let option = projectOptions.find(opt => opt.name.startsWith(projectName));
    
    // If not found in projectOptions, check in static options
    if (!option) {
      option = staticProjectOptions.find(opt => opt.name.startsWith(projectName));
    }
    
    if (option) {
      // Set the selected project
      setSelectedProject(option);
      
      // Format the search term as NAME(TYPE)
      const formattedName = `${option.name}(${option.type})`;
      setSearchTerm(formattedName);
      
      // Trigger search with the formatted name
      searchProjects(option.name.split(' ')[0]); // Still search with first word for better results
      setIsDropdownOpen(false);
      
      // Focus the input field after selection for better UX
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  // Expose form data and template generation to the parent component via ref
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      if (isFormValid && selectedProject) {
        return {
          project: selectedProject.name,
          projectName: selectedProject.name,
          projectType: selectedProject.type
        };
      }
      return null;
    },
    generateTemplate: () => {
      if (isFormValid && selectedProject) {
        return generateTemplateString({
          project: selectedProject.name,
          projectName: selectedProject.name,
          projectType: selectedProject.type
        });
      }
      return null;
    }
  }));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <main className="body-container">
      {/* Project Search Section */}
      <div className="form-section">
        <label className="form-label">Select Project or Ecosystem</label>
        <div className="search-container" ref={dropdownRef}>
          <div className="input-container">
            {selectedProject && selectedProject.image && (
              <div className="selected-token-icon">
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.name} 
                  className="selected-crypto-icon" 
                  onError={(e) => {
                    // Handle image loading error
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            <input
              type="text"
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
              onFocus={() => setIsDropdownOpen(searchTerm.length > 0 && filteredOptions.length > 0)}
              className={`form-input ${selectedProject && selectedProject.image ? 'with-icon' : ''}`}
              placeholder="Enter project or ecosystem name"
            />
          </div>
          
          {/* Search Dropdown */}
          {isDropdownOpen && (
            <div className="search-dropdown">
              {isLoading ? (
                <div className="dropdown-item loading">Searching...</div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div 
                    key={option.name} 
                    className="dropdown-item"
                    onClick={() => handleSelectProject(option)}
                  >
                    <div className="token-icon-container">
                      {option.image ? (
                        <img 
                          src={option.image} 
                          alt={option.name} 
                          className="crypto-icon" 
                          onError={(e) => {
                            // Handle image loading error
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="crypto-icon-placeholder"></div>
                      )}
                    </div>
                    <div className="token-details">
                      <div className="crypto-info">
                        <span className="crypto-name">{option.name}</span>
                        <span className="crypto-symbol">/{option.type}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="dropdown-item no-results">No projects found</div>
              )}
            </div>
          )}
        </div>
        
        {/* Candidate Project List */}
        <div className="candidate-projects">
          {isLoading ? (
            <div className="loading-indicator">Loading...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            // Show popular projects or the filtered results if available
            staticProjectOptions.map((option) => (
                <button
                  key={option.name}
                  className={`project-button ${selectedProject?.name === option.name ? 'selected' : ''}`}
                  onClick={() => handleCandidateProjectSelect(option.name.split(' ')[0])}
                >
                  {option.name}
                </button>
              ))
          )}
        </div>
      </div>
    </main>
  );
});

ProjectTracker.displayName = 'ProjectTracker';

export default ProjectTracker;
